import { useState } from "react";
import { useAccount, useChainId, usePublicClient, useSwitchChain, useWriteContract } from "wagmi";
import { isAddress, type Address } from "viem";
import { ERC721DropAbi } from "@atelier/abis";
import type { ChainConfig } from "@atelier/config";

/**
 * Owner-only ERC721 transfer form. The parent decides when to render this
 * (typically when ownerOf == connected wallet) and provides a refresh
 * callback so reads can re-run after the transfer lands on-chain.
 */
export function TransferForm({
  chain,
  contractAddress,
  tokenId,
  onTransferred,
}: {
  chain: ChainConfig;
  contractAddress: Address;
  tokenId: number;
  onTransferred: () => void;
}) {
  const { address: wallet, isConnected } = useAccount();
  const walletChainId = useChainId();
  const { switchChain } = useSwitchChain();
  const pc = usePublicClient({ chainId: chain.chainId });
  const { writeContractAsync, isPending } = useWriteContract();

  const [transferTo, setTransferTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  const transfer = async () => {
    if (!isConnected || !wallet) return;
    if (walletChainId !== chain.chainId) {
      switchChain({ chainId: chain.chainId });
      return;
    }
    if (!isAddress(transferTo)) {
      setStatus("Recipient is not a valid address.");
      return;
    }
    setBusy(true);
    setStatus("Confirm in wallet…");
    try {
      const hash = await writeContractAsync({
        address: contractAddress,
        abi: ERC721DropAbi,
        functionName: "safeTransferFrom",
        args: [wallet, transferTo as Address, BigInt(tokenId)],
        chainId: chain.chainId,
      });
      setStatus("Confirming on-chain…");
      if (pc) await pc.waitForTransactionReceipt({ hash });
      setStatus(`Transferred to ${transferTo}.`);
      setTransferTo("");
      onTransferred();
    } catch (err) {
      setStatus(`Error: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section>
      <h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Transfer
      </h2>
      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          className="input h-9 flex-1"
          placeholder="0x… recipient"
          value={transferTo}
          disabled={busy || isPending}
          onChange={(e) => setTransferTo(e.target.value)}
        />
        <button
          className="btn-primary h-9 px-3 text-xs"
          disabled={busy || isPending || !transferTo}
          onClick={() => void transfer()}
        >
          {busy || isPending ? "Working…" : "Transfer"}
        </button>
      </div>
      {status && <div className="mt-2 text-xs text-muted-foreground">{status}</div>}
    </section>
  );
}
